/**
 * quick-reply-csv.ts — Đọc / ghi CSV cho Thư viện trả lời nhanh (thuần, không React).
 *
 * - Xuất: UTF-8 có BOM + xuống dòng CRLF để Excel mở đúng tiếng Việt.
 * - Nhập: chấp nhận dấu phân cách `,` `;` hoặc tab (Excel máy locale Việt Nam
 *   thường lưu CSV bằng `;`), ô có ngoặc kép / xuống dòng theo RFC 4180.
 * - Chống "CSV injection": ô bắt đầu bằng = + @ được thêm `'` khi xuất, và
 *   được gỡ lại khi nhập để đi một vòng xuất → nhập không đổi nội dung.
 */

import { i18n } from '@/lib/i18n'

// Tiêu đề cột là định dạng dữ liệu file — giữ nguyên để file cũ vẫn nhập được
export const CSV_HEADERS = ['Tiêu đề', 'Nội dung', 'Danh mục'] as const // i18n-ignore

export interface CsvQuickReply {
  title: string
  content: string
  category: string | null
}

export interface PreviewRow extends CsvQuickReply {
  /** Số dòng trong file (tính cả dòng tiêu đề) — để người dùng dò lại trong Excel. */
  line: number
  status: 'ok' | 'error' | 'duplicate' | 'exists'
  message?: string
}

export interface ParsedCsv {
  rows: PreviewRow[]
  /** Lỗi cả file (không nhận ra cột bắt buộc, file rỗng…). */
  fatal?: string
}

const BOM = '﻿'
const FORMULA_START = /^[=+@]/

/** Khoá so trùng tiêu đề — cùng quy tắc với backend (quick-reply-service.titleKey). */
export function titleKey(title: string): string {
  return title.trim().replace(/\s+/g, ' ').toLocaleLowerCase('vi')
}

function escapeCell(value: string): string {
  const safe = FORMULA_START.test(value) ? `'${value}` : value
  return /[",\r\n;\t]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe
}

/** Dựng nội dung file CSV (đã có BOM). */
export function serializeQuickRepliesCsv(items: CsvQuickReply[]): string {
  const lines = [CSV_HEADERS.join(',')]
  for (const it of items) {
    lines.push([it.title, it.content, it.category ?? ''].map(escapeCell).join(','))
  }
  return BOM + lines.join('\r\n') + '\r\n'
}

/** Đoán dấu phân cách từ dòng đầu (ngoài ngoặc kép). */
function detectDelimiter(text: string): string {
  const counts: Record<string, number> = { ',': 0, ';': 0, '\t': 0 }
  let inQuotes = false
  for (const ch of text) {
    if (ch === '"') inQuotes = !inQuotes
    else if (!inQuotes && (ch === '\n' || ch === '\r')) break
    else if (!inQuotes && ch in counts) counts[ch]++
  }
  const [best, n] = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]
  return n > 0 ? best : ','
}

/** Tách CSV thành mảng dòng × ô (RFC 4180). Bỏ BOM, bỏ dòng trống hoàn toàn. */
export function parseCsv(input: string): string[][] {
  const text = input.startsWith(BOM) ? input.slice(1) : input
  const delim = detectDelimiter(text)
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let inQuotes = false

  const pushRow = () => {
    row.push(cell)
    if (row.some((c) => c.trim() !== '')) rows.push(row)
    row = []
    cell = ''
  }

  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"'
          i++
        } else inQuotes = false
      } else cell += ch
    } else if (ch === '"' && cell === '') inQuotes = true
    else if (ch === delim) {
      row.push(cell)
      cell = ''
    } else if (ch === '\r') {
      if (text[i + 1] === '\n') i++
      pushRow()
    } else if (ch === '\n') pushRow()
    else cell += ch
  }
  if (cell !== '' || row.length) pushRow()
  return rows
}

function fold(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/gi, 'd')
    .trim()
    .toLowerCase()
}

const HEADER_ALIASES: Record<keyof CsvQuickReply, string[]> = {
  title: ['tieu de', 'title', 'ten', 'ten mau'],
  content: ['noi dung', 'content', 'message', 'tin nhan'],
  category: ['danh muc', 'category', 'nhom', 'phan loai'],
}

function unescapeCell(v: string): string {
  return v.startsWith("'") && FORMULA_START.test(v.slice(1)) ? v.slice(1) : v
}

/**
 * Đọc file CSV thành danh sách xem trước. `existingTitles` = tiêu đề đang có
 * trong thư viện (để đánh dấu "Đã có"). Dòng trùng tiêu đề trong file: giữ dòng đầu.
 */
export function parseQuickReplyCsv(text: string, existingTitles: string[] = []): ParsedCsv {
  const table = parseCsv(text)
  if (table.length === 0) return { rows: [], fatal: i18n.t('quickReplies:csv.emptyFile') }

  const header = table[0].map(fold)
  const col = (k: keyof CsvQuickReply) => header.findIndex((h) => HEADER_ALIASES[k].includes(h))
  const idx = { title: col('title'), content: col('content'), category: col('category') }
  if (idx.title < 0 || idx.content < 0) {
    return { rows: [], fatal: i18n.t('quickReplies:csv.missingColumns') }
  }

  const existing = new Set(existingTitles.map(titleKey))
  const seen = new Set<string>()
  const rows: PreviewRow[] = table.slice(1).map((cells, i) => {
    const get = (j: number) => (j < 0 ? '' : unescapeCell(cells[j] ?? ''))
    const title = get(idx.title).trim().replace(/\s+/g, ' ')
    const content = get(idx.content).replace(/\r\n?/g, '\n').trim()
    const category = get(idx.category).trim() || null
    const base = { line: i + 2, title, content, category }

    if (!title) return { ...base, status: 'error', message: i18n.t('quickReplies:csv.missingTitle') }
    if (!content) return { ...base, status: 'error', message: i18n.t('quickReplies:csv.missingContent') }
    const k = titleKey(title)
    if (seen.has(k)) return { ...base, status: 'duplicate', message: i18n.t('quickReplies:csv.duplicateInFile') }
    seen.add(k)
    if (existing.has(k)) return { ...base, status: 'exists', message: i18n.t('quickReplies:csv.existsInLibrary') }
    return { ...base, status: 'ok' }
  })
  return { rows }
}

/** Tải chuỗi CSV xuống máy người dùng. */
export function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
